import { useApp, useContentStore, useEditorSync, useProblem, useSelectionReference, useUploadJobs, useS3Client } from "@/hooks/contextHooks";
import { AppMeta, Content, NewContentItem, QuizMeta, QuizQuestion } from "@xnav/types/src/common";
import { ReactStateType } from "@xnav/types/src/frontend";
import { EditContent } from "@/pages/editor";
import { useEffect, useState } from "react";
import Loading from "./loading";
import { createClientContent, createServerContent } from "@/functions/editorFunctions";
import { apiGetRequest, apiPutRequest, apiPostRequest } from "@/functions/requests";
import { v4 } from "uuid";
import { Box, Button, Flex, FormControl, FormLabel, Heading, IconButton, Input, Radio, RadioGroup, Stack, Text, Textarea, VStack, Card, CardBody, CardHeader, useColorModeValue, Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, useDisclosure, Image, SimpleGrid, Menu, MenuButton, MenuList, MenuItem, Portal, useToast, InputGroup, InputLeftElement, InputRightElement, Badge, Checkbox, ModalHeader, HStack, MenuDivider, Tooltip } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faListCheck, faCheck, faSave, faUpload, faDownload, faCrop, faPen, faChevronDown, faSearch, faTimes, faInfo, faMusic, faVolumeHigh, faPlay, faPause, faFileAudio, faEye, faImage, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { useAuthenticator } from "@aws-amplify/ui-react";
import React, { Suspense, lazy, useRef, useCallback } from "react";
import Papa from 'papaparse';

import QuestionCard from "./quizQuestionCard";
import ImageAnnotator from "./ImageAnnotator";
import SignedImage, { getSignedS3Url } from "./signedImage";
import QuizAudioPlayer from "./quizAudioPlayer";

export default function QuizEditor() {
    const app = useApp() as AppMeta
    const [, setProblem] = useProblem()
    const [content, setContent] = useState<EditContent>()
    const [searchQuery, setSearchQuery] = useState<string>("")
    const toast = useToast()

    const [selectionReference,] = useSelectionReference() as ReactStateType<NewContentItem | undefined>
    const [pushJob, popJob] = useUploadJobs()

    const [contentStore, setContentStore] = useContentStore() as ReactStateType<Record<string, EditContent>>
    const [editorSync,] = useEditorSync()
    const s3 = useS3Client() as S3Client
    const { user } = useAuthenticator()

    const { isOpen: isAnnotateOpen, onOpen: onAnnotateOpen, onClose: onAnnotateClose } = useDisclosure()
    const [annotateTarget, setAnnotateTarget] = useState<{ qIndex: number, imgIndex: number, url: string } | null>(null)
    const [descriptionTarget, setDescriptionTarget] = useState<{ qIndex: number, imgIndex: number } | null>(null)
    const [previewLightboxUrl, setPreviewLightboxUrl] = useState<string | null>(null)
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([])
    const { isOpen: isClearConfirmOpen, onOpen: onClearConfirmOpen, onClose: onClearConfirmClose } = useDisclosure()
    const [questionToDelete, setQuestionToDelete] = useState<number | null>(null)

    async function fetchContent() {
        if (!selectionReference || selectionReference.type !== 'quiz') return
        const temp: Content = {
            id: selectionReference.id,
            title: selectionReference.title,
            hotSpots: [],
            polygons: [],
            wisdomDocTitles: [],
            quizData: { questions: [] }
        }

        let loaded: Content = temp

        try {
            // S3 Bypass Fix for Quiz
            const command = new GetObjectCommand({
                Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                Key: `${app.id}/contents/content/${selectionReference.id}.json`
            })
            const url = await getSignedUrl(s3 as any, command as any, { expiresIn: 360 })
            const response = await fetch(url)
            if (response.ok) {
                const strData = await response.text()
                let parsed = JSON.parse(strData)
                if (!parsed.quizData) {
                    parsed.quizData = { questions: [] }
                }
                loaded = parsed
            } else {
                throw new Error("S3 fetch failed with status: " + response.status)
            }
        } catch (e) {
            console.log("S3 direct fetch failed for quiz, trying API", e)
            try {
                let result = await apiGetRequest<string, Content | string>(`/content/${app.id}/${selectionReference.id}`).catch(() => undefined)

                if (result && result !== '') {
                    if (typeof result === 'string') {
                        try {
                            result = JSON.parse(result)
                        } catch (e) {
                            console.error("Failed to parse quiz content:", e)
                        }
                    }
                    if (result && typeof result === 'object') {
                        const loadedContent = result as Content;
                        if (!loadedContent.quizData) {
                            loadedContent.quizData = { questions: [] }
                        }
                        loaded = loadedContent
                    }
                }
            } catch (error) {
                console.error("Error fetching quiz:", error)
            }
        }

        const clientContent = createClientContent(loaded)
        setContent(clientContent)
        setContentStore(prev => ({ ...prev, [clientContent.id]: clientContent }))
    }

    async function pushContent() {
        if (!content) return
        pushJob()
        await apiPutRequest<Content, undefined>(`/content/${app.id}/${content.id}`, createServerContent(content)).catch(setProblem)
        popJob()
    }

    useEffect(() => {
        if (!selectionReference || selectionReference.type !== 'quiz') {
            setContent(undefined)
            return
        }
        if (contentStore[selectionReference.id]) {
            // Ensure stored content has quiz structure
            const stored = contentStore[selectionReference.id]
            if (!stored.quizData) {
                stored.quizData = { questions: [] }
            }
            setContent(stored)
            return
        }
        fetchContent()
    }, [selectionReference?.id])

    const pendingContentRef = useRef<EditContent | null>(null);

    // Debounced sync to global contentStore to prevent re-rendering parent Editor and sidebar on every keystroke
    useEffect(() => {
        if (!content) return;
        pendingContentRef.current = content;

        const timer = setTimeout(() => {
            setContentStore(prev => {
                if (prev[content.id] === content) return prev;
                return { ...prev, [content.id]: content };
            });
        }, 400);

        return () => clearTimeout(timer);
    }, [content, setContentStore]);

    // Immediately flush pending changes to contentStore when save is triggered or component unmounts
    useEffect(() => {
        const handleSave = () => {
            if (pendingContentRef.current) {
                const c = pendingContentRef.current;
                setContentStore(prev => ({ ...prev, [c.id]: c }));
            }
        };

        window.addEventListener('editor-save', handleSave);
        return () => {
            window.removeEventListener('editor-save', handleSave);
            if (pendingContentRef.current) {
                const c = pendingContentRef.current;
                setContentStore(prev => ({ ...prev, [c.id]: c }));
            }
        };
    }, [setContentStore]);

    useEffect(() => {
        if (editorSync) pushContent()
    }, [editorSync])

    // Update Helpers
    const updateQuestions = (newQuestions: QuizQuestion[]) => {
        if (!content) return;
        setContent({
            ...content,
            quizData: {
                ...content.quizData,
                questions: newQuestions
            }
        } as EditContent);
    };

    const parseLevel = (val: string): string => {
        const clean = (val || "").trim().toLowerCase();
        if (clean.includes("3") || clean.includes("hard")) return "Level 3";
        if (clean.includes("2") || clean.includes("med")) return "Level 2";
        return "Level 1";
    };

    const addQuestion = (type: 'type1' | 'type2' | 'survey' = 'type1') => {
        if (!content?.quizData) return;
        const newQ: QuizQuestion = {
            id: v4(),
            text: type === 'survey' ? "New Survey Question" : "New Question",
            options: ["Option A", "Option B", "Option C", "Option D"],
            wrongExplanations: ["", "", "", ""],
            correctAnswer: type === 'survey' ? -1 : 0,
            explanation: "",
            images: [],
            questionType: type,
            level: 'Level 1'
        };
        updateQuestions([...content.quizData.questions, newQ]);
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !content?.quizData) return;

        Papa.parse(file, {
            header: false,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = results.data as string[][];
                // Skip header row if it contains "Question" or "Option"
                const dataRows = rows.filter(row => row.length > 1);
                let startIndex = 0;
                if (dataRows.length > 0 && 
                    (dataRows[0][1]?.toLowerCase().includes("question") || dataRows[0][2]?.toLowerCase().includes("option"))) {
                    startIndex = 1;
                }

                const newQuestions: QuizQuestion[] = [];
                for (let i = startIndex; i < dataRows.length; i++) {
                    const row = dataRows[i];
                    if (row.length < 6) continue; // Basic validation

                    const questionText = row[1] || "";
                    if (!questionText.trim()) continue;

                    let newQ: QuizQuestion;
                    if (row.length >= 11) {
                        // Type 1 structure (12 columns + optional 13th for level)
                        const optionA = row[2] || "";
                        const wrongA = row[3] || "";
                        const optionB = row[4] || "";
                        const wrongB = row[5] || "";
                        const optionC = row[6] || "";
                        const wrongC = row[7] || "";
                        const optionD = row[8] || "";
                        const wrongD = row[9] || "";
                        
                        const correctLetter = (row[10] || "").trim().toUpperCase();
                        let correctAnswerIndex = 0;
                        if (correctLetter === 'B') correctAnswerIndex = 1;
                        else if (correctLetter === 'C') correctAnswerIndex = 2;
                        else if (correctLetter === 'D') correctAnswerIndex = 3;

                        const explanation = row[11] || "";
                        const levelRaw = row[12] || "Level 1";
                        const level = parseLevel(levelRaw);

                        newQ = {
                            id: v4(),
                            text: questionText,
                            options: [optionA, optionB, optionC, optionD],
                            wrongExplanations: [wrongA, wrongB, wrongC, wrongD],
                            correctAnswer: correctAnswerIndex,
                            explanation: explanation,
                            images: [],
                            questionType: 'type1',
                            level: level
                        };
                    } else if (row.length >= 7 && ['A', 'B', 'C', 'D'].includes((row[6] || "").trim().toUpperCase())) {
                        // Type 2 structure (8 columns + optional 9th for level)
                        const optionA = row[2] || "";
                        const optionB = row[3] || "";
                        const optionC = row[4] || "";
                        const optionD = row[5] || "";
                        
                        const correctLetter = (row[6] || "").trim().toUpperCase();
                        let correctAnswerIndex = 0;
                        if (correctLetter === 'B') correctAnswerIndex = 1;
                        else if (correctLetter === 'C') correctAnswerIndex = 2;
                        else if (correctLetter === 'D') correctAnswerIndex = 3;

                        const explanation = row[7] || "";
                        const levelRaw = row[8] || "Level 1";
                        const level = parseLevel(levelRaw);

                        newQ = {
                            id: v4(),
                            text: questionText,
                            options: [optionA, optionB, optionC, optionD],
                            wrongExplanations: ["", "", "", ""],
                            correctAnswer: correctAnswerIndex,
                            explanation: explanation,
                            images: [],
                            questionType: 'type2',
                            level: level
                        };
                    } else {
                        // Survey structure (6 columns: Num, Text, OptA, OptB, OptC, OptD, optional Level)
                        const optionA = row[2] || "";
                        const optionB = row[3] || "";
                        const optionC = row[4] || "";
                        const optionD = row[5] || "";
                        const levelRaw = row[6] || "Level 1";
                        const level = parseLevel(levelRaw);

                        newQ = {
                            id: v4(),
                            text: questionText,
                            options: [optionA, optionB, optionC, optionD],
                            wrongExplanations: ["", "", "", ""],
                            correctAnswer: -1,
                            explanation: "",
                            images: [],
                            questionType: 'survey',
                            level: level
                        };
                    }
                    newQuestions.push(newQ);
                }

                if (newQuestions.length > 0) {
                    updateQuestions([...(content.quizData?.questions || []), ...newQuestions]);
                    toast({
                        title: `Imported ${newQuestions.length} questions successfully!`,
                        status: "success",
                        duration: 3000,
                        isClosable: true
                    });
                }
                
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            },
            error: (error) => {
                console.error("Error parsing CSV:", error);
            }
        });
    };

    const downloadTemplate = (type: 'type1' | 'type2' | 'survey') => {
        let template = "";
        let filename = "";
        if (type === 'type1') {
            template = "Question Number,Question Text,Option A,Why A is wrong,Option B,Why B is wrong,Option C,Why C is wrong,Option D,Why D is wrong,Correct Option (A/B/C/D),Explanation,Level\n1,What is the powerhouse of the cell?,Nucleus,The nucleus stores DNA.,Mitochondria,,Ribosome,Ribosomes make proteins.,Endoplasmic Recticulum,The ER is for transport.,B,Mitochondria generate ATP which powers the cell.,Level 1\n2,Which planet is known as the Red Planet?,Earth,Earth is the blue planet.,Venus,Venus is extremely hot.,Jupiter,Jupiter is a gas giant.,Mars,,D,Mars appears red due to iron oxide.,Level 2";
            filename = 'quiz_detailed_template.csv';
        } else if (type === 'type2') {
            template = "Question Number,Question Text,Option A,Option B,Option C,Option D,Correct Option (A/B/C/D),Explanation,Level\n1,What is the powerhouse of the cell?,Nucleus,Mitochondria,Ribosome,Endoplasmic Recticulum,B,Mitochondria generate ATP which powers the cell.,Level 1\n2,Which planet is known as the Red Planet?,Earth,Venus,Jupiter,Mars,D,Mars appears red due to iron oxide.,Level 2";
            filename = 'quiz_simple_template.csv';
        } else {
            template = "Question Number,Question Text,Option A,Option B,Option C,Option D,Level\n1,How confident are you identifying fetal CRL on ultrasound?,Very Confident,Somewhat Confident,Neutral,Not Confident,Level 1\n2,Which ultrasound transducer would you select for this scan?,Linear Probe,Curvilinear Probe,Phased Array,Endocavitary,Level 2";
            filename = 'survey_template.csv';
        }
        const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const updateQuestion = useCallback((index: number, field: keyof QuizQuestion, value: any) => {
        setContent(prev => {
            if (!prev?.quizData) return prev;
            const updated = [...prev.quizData.questions];
            if (!updated[index]) return prev;
            updated[index] = { ...updated[index], [field]: value };
            return {
                ...prev,
                quizData: {
                    ...prev.quizData,
                    questions: updated
                }
            };
        });
    }, []);

    const updateOption = useCallback((qIndex: number, optIndex: number, value: string) => {
        setContent(prev => {
            if (!prev?.quizData) return prev;
            const updated = [...prev.quizData.questions];
            if (!updated[qIndex]) return prev;
            const newOptions = [...updated[qIndex].options];
            newOptions[optIndex] = value;
            updated[qIndex] = { ...updated[qIndex], options: newOptions };
            return {
                ...prev,
                quizData: {
                    ...prev.quizData,
                    questions: updated
                }
            };
        });
    }, []);

    const updateWrongExplanation = useCallback((qIndex: number, optIndex: number, value: string) => {
        setContent(prev => {
            if (!prev?.quizData) return prev;
            const updated = [...prev.quizData.questions];
            if (!updated[qIndex]) return prev;
            const newExplanations = [...(updated[qIndex].wrongExplanations || ["", "", "", ""])];
            newExplanations[optIndex] = value;
            updated[qIndex] = { ...updated[qIndex], wrongExplanations: newExplanations };
            return {
                ...prev,
                quizData: {
                    ...prev.quizData,
                    questions: updated
                }
            };
        });
    }, []);

    const handleImageUpload = useCallback(async (qIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const files = Array.from(e.target.files);
        
        const uploadToastId = toast({
            title: "Uploading image(s)...",
            status: "info",
            duration: null,
            isClosable: false,
            position: "top-right"
        });

        try {
            const newImages = await Promise.all(files.map(file => new Promise<any>(async (resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = async () => {
                    try {
                        const base64Data = (reader.result as string).split(',')[1];
                        const response = await apiPostRequest<any, { imageUrl: string, fileKey: string }>('/upload-image', {
                            fileName: file.name,
                            fileContent: base64Data
                        });
                        resolve({ url: response.imageUrl, tag: '', originalUrl: response.imageUrl });
                    } catch (err) {
                        reject(err);
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            })));

            setContent(prev => {
                if (!prev?.quizData) return prev;
                const updated = [...prev.quizData.questions];
                if (!updated[qIndex]) return prev;
                updated[qIndex] = { ...updated[qIndex], images: [...(updated[qIndex].images || []), ...newImages] };
                return {
                    ...prev,
                    quizData: {
                        ...prev.quizData,
                        questions: updated
                    }
                };
            });

            toast.update(uploadToastId, {
                title: "Upload complete!",
                status: "success",
                duration: 3000,
                isClosable: true
            });
        } catch (error) {
            console.error("Failed to upload image(s):", error);
            toast.update(uploadToastId, {
                title: "Upload failed",
                description: "Failed to upload one or more images.",
                status: "error",
                duration: 5000,
                isClosable: true
            });
        }
    }, [toast]);

    const removeImage = useCallback((qIndex: number, imgIndex: number) => {
        setContent(prev => {
            if (!prev?.quizData) return prev;
            const updated = [...prev.quizData.questions];
            if (!updated[qIndex]) return prev;
            const newImages = [...(updated[qIndex].images || [])];
            newImages.splice(imgIndex, 1);
            updated[qIndex] = { ...updated[qIndex], images: newImages };
            return {
                ...prev,
                quizData: {
                    ...prev.quizData,
                    questions: updated
                }
            };
        });
    }, []);

    const handleAudioUpload = useCallback(async (qIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        
        const uploadToastId = toast({
            title: "Uploading audio clip...",
            status: "info",
            duration: null,
            isClosable: false,
            position: "top-right"
        });

        try {
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const base64Data = (reader.result as string).split(',')[1];
                    const response = await apiPostRequest<any, { imageUrl: string, fileKey: string }>('/upload-image', {
                        fileName: file.name,
                        fileContent: base64Data
                    });
                    setContent(prev => {
                        if (!prev?.quizData) return prev;
                        const updated = [...prev.quizData.questions];
                        if (!updated[qIndex]) return prev;
                        updated[qIndex] = {
                            ...updated[qIndex],
                            audioUrl: response.imageUrl,
                            audioTitle: file.name
                        };
                        return {
                            ...prev,
                            quizData: {
                                ...prev.quizData,
                                questions: updated
                            }
                        };
                    });
                    toast.update(uploadToastId, {
                        title: "Audio uploaded successfully!",
                        status: "success",
                        duration: 3000,
                        isClosable: true
                    });
                } catch (err) {
                    console.error("Failed to upload audio:", err);
                    toast.update(uploadToastId, {
                        title: "Audio upload failed",
                        description: "Could not upload audio file.",
                        status: "error",
                        duration: 5000,
                        isClosable: true
                    });
                }
            };
            reader.readAsDataURL(file);
        } catch (err) {
            console.error("Audio file read error:", err);
        }
    }, [toast]);

    const removeAudio = useCallback((qIndex: number) => {
        setContent(prev => {
            if (!prev?.quizData) return prev;
            const updated = [...prev.quizData.questions];
            if (!updated[qIndex]) return prev;
            updated[qIndex] = {
                ...updated[qIndex],
                audioUrl: undefined,
                audioTitle: undefined
            };
            return {
                ...prev,
                quizData: {
                    ...prev.quizData,
                    questions: updated
                }
            };
        });
        toast({
            title: "Audio removed",
            status: "info",
            duration: 2000,
            isClosable: true
        });
    }, [toast]);

    const handleOpenEditor = useCallback(async (qIndex: number, imgIndex: number, url: string) => {
        let finalUrl = url;
        if (url.startsWith('http') || !url.startsWith('data:')) {
            try {
                const signedUrlStr = await getSignedS3Url(s3, url);
                const res = await fetch(signedUrlStr || url);
                const blob = await res.blob();
                finalUrl = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.error("Failed to load image as blob for editing, using original URL", e);
            }
        }
        setAnnotateTarget({ qIndex, imgIndex, url: finalUrl });
        onAnnotateOpen();
    }, [s3, onAnnotateOpen]);

    const handleCloseAnnotator = () => {
        setAnnotateTarget(null);
        onAnnotateClose();
    };

    const confirmDeleteQuestion = useCallback((index: number) => {
        setContent(prev => {
            if (!prev?.quizData) return prev;
            const updated = prev.quizData.questions.filter((_, i) => i !== index);
            return {
                ...prev,
                quizData: {
                    ...prev.quizData,
                    questions: updated
                }
            };
        });
        toast({
            title: "Question deleted",
            status: "info",
            duration: 2500,
            isClosable: true
        });
    }, [toast]);

    const deleteQuestion = useCallback((index: number) => {
        setQuestionToDelete(index);
    }, []);

    const handleToggleSelect = useCallback((id: string) => {
        setSelectedQuestionIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    }, []);

    const handleOpenLightbox = useCallback(async (url: string) => {
        const signed = await getSignedS3Url(s3, url);
        setPreviewLightboxUrl(signed || url);
    }, [s3]);

    const handleClearAllQuestions = () => {
        updateQuestions([]);
        setSelectedQuestionIds([]);
        onClearConfirmClose();
        toast({
            title: "Cleared all questions",
            status: "info",
            duration: 3000,
            isClosable: true,
        });
    };

    const exportToCsv = () => {
        if (!content?.quizData || content.quizData.questions.length === 0) return;
        
        const hasType1 = content.quizData.questions.some(q => q.questionType === 'type1');
        
        let headers: string[];
        let rows: any[][];

        if (hasType1) {
            headers = [
                "Question Number",
                "Question Text",
                "Option A",
                "Why A is wrong",
                "Option B",
                "Why B is wrong",
                "Option C",
                "Why C is wrong",
                "Option D",
                "Why D is wrong",
                "Correct Option (A/B/C/D)",
                "Explanation",
                "Level"
            ];
            
            rows = content.quizData.questions.map((q, index) => {
                const getWrongExplanation = (optIndex: number) => {
                    if (q.questionType === 'type2' || q.questionType === 'survey') return "";
                    return (q.wrongExplanations && q.wrongExplanations[optIndex]) || "";
                };
                
                const correctLetter = q.questionType === 'survey' || q.correctAnswer === undefined || q.correctAnswer === -1
                    ? ""
                    : String.fromCharCode(65 + q.correctAnswer);
                const levelVal = q.level ? q.level.replace("Level ", "") : "1";
                const expVal = q.questionType === 'survey' ? "" : (q.explanation || "");
                
                return [
                    index + 1,
                    q.text,
                    q.options[0] || "",
                    getWrongExplanation(0),
                    q.options[1] || "",
                    getWrongExplanation(1),
                    q.options[2] || "",
                    getWrongExplanation(2),
                    q.options[3] || "",
                    getWrongExplanation(3),
                    correctLetter,
                    expVal,
                    levelVal
                ];
            });
        } else {
            headers = [
                "Question Number",
                "Question Text",
                "Option A",
                "Option B",
                "Option C",
                "Option D",
                "Correct Option (A/B/C/D)",
                "Explanation",
                "Level"
            ];
            
            rows = content.quizData.questions.map((q, index) => {
                const correctLetter = q.questionType === 'survey' || q.correctAnswer === undefined || q.correctAnswer === -1
                    ? ""
                    : String.fromCharCode(65 + q.correctAnswer);
                const levelVal = q.level ? q.level.replace("Level ", "") : "1";
                const expVal = q.questionType === 'survey' ? "" : (q.explanation || "");
                
                return [
                    index + 1,
                    q.text,
                    q.options[0] || "",
                    q.options[1] || "",
                    q.options[2] || "",
                    q.options[3] || "",
                    correctLetter,
                    expVal,
                    levelVal
                ];
            });
        }
        
        const escapeCsvCell = (val: any) => {
            const str = String(val);
            if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };
        
        const csvContent = [
            headers.map(escapeCsvCell).join(","),
            ...rows.map(row => row.map(escapeCsvCell).join(","))
        ].join("\n");
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const suffix = hasType1 ? "detailed" : "simple";
        const safeTitle = (content.title || "quiz").toLowerCase().replace(/[^a-z0-9]+/g, "_");
        a.download = `quiz_export_${safeTitle}_${suffix}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast({
            title: `Quiz exported successfully in ${suffix} format!`,
            status: "success",
            duration: 3000,
            isClosable: true
        });
    };

    if (!selectionReference || selectionReference.type !== 'quiz') return <></>
    if (!content) return <Loading message="Fetching quiz..." />
    if (!content.quizData) return <Loading message="Initializing quiz..." />

    const filteredQuestions = (content.quizData.questions || [])
        .map((q, originalIndex) => ({ q, originalIndex }))
        .filter(({ q, originalIndex }) => {
            if (!searchQuery.trim()) return true;
            const query = searchQuery.toLowerCase().trim();
            const textMatch = q.text?.toLowerCase().includes(query);
            const optionsMatch = q.options?.some(opt => opt.toLowerCase().includes(query));
            const explanationMatch = q.explanation?.toLowerCase().includes(query);
            const wrongExplanationsMatch = q.wrongExplanations?.some(w => w?.toLowerCase().includes(query));
            const indexMatch = `question ${originalIndex + 1}`.includes(query) || `${originalIndex + 1}` === query;
            return textMatch || optionsMatch || explanationMatch || wrongExplanationsMatch || indexMatch;
        });

    return (
        <Flex direction="column" w="100%" h="100%" p={8} overflowY="auto" bg={useColorModeValue("gray.50", "gray.900")} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
            <Box maxW="1000px" w="100%" mx="auto">
                <Flex justify="space-between" align="center" mb={6}>
                    <Box>
                        <Heading 
                            size="2xl" 
                            bgGradient="linear(to-r, #0284c7, #0ea5e9, #10b981)" 
                            bgClip="text"
                            fontWeight="extrabold"
                            letterSpacing="tight"
                        >
                            Quiz Engine
                        </Heading>
                        <Text color="gray.500" mt={2} fontSize="lg">Craft engaging assessments for {content.title}</Text>
                    </Box>
                    <Flex gap={4} align="center">
                        <Menu>
                            <MenuButton 
                                as={Button} 
                                variant="outline" 
                                colorScheme="teal" 
                                size="sm" 
                                borderRadius="xl"
                                rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                                borderColor={useColorModeValue("teal.300", "teal.600")}
                                _hover={{ bg: useColorModeValue("teal.50", "whiteAlpha.100") }}
                            >
                                CSV Actions
                            </MenuButton>
                            <Portal>
                                <MenuList 
                                    zIndex={9999}
                                    bg={useColorModeValue("white", "#131b2e")}
                                    borderColor={useColorModeValue("gray.200", "whiteAlpha.200")}
                                    border="1px solid"
                                    borderRadius="2xl"
                                    shadow="2xl"
                                    p={2}
                                    minW="280px"
                                >
                                    <MenuItem 
                                        icon={<FontAwesomeIcon icon={faDownload} />}
                                        onClick={() => downloadTemplate('type1')}
                                        borderRadius="xl"
                                        py={2.5}
                                        px={3}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        bg="transparent"
                                        color={useColorModeValue("gray.800", "gray.100")}
                                        _hover={{ bg: useColorModeValue("teal.50", "whiteAlpha.150"), color: useColorModeValue("teal.700", "cyan.300") }}
                                        _focus={{ bg: useColorModeValue("teal.50", "whiteAlpha.150"), color: useColorModeValue("teal.700", "cyan.300") }}
                                    >
                                        Download Detailed Template (Type 1)
                                    </MenuItem>
                                    <MenuItem 
                                        icon={<FontAwesomeIcon icon={faDownload} />}
                                        onClick={() => downloadTemplate('type2')}
                                        borderRadius="xl"
                                        py={2.5}
                                        px={3}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        bg="transparent"
                                        color={useColorModeValue("gray.800", "gray.100")}
                                        _hover={{ bg: useColorModeValue("teal.50", "whiteAlpha.150"), color: useColorModeValue("teal.700", "cyan.300") }}
                                        _focus={{ bg: useColorModeValue("teal.50", "whiteAlpha.150"), color: useColorModeValue("teal.700", "cyan.300") }}
                                    >
                                        Download Simple Template (Type 2)
                                    </MenuItem>
                                    <MenuItem 
                                        icon={<FontAwesomeIcon icon={faDownload} />}
                                        onClick={() => downloadTemplate('survey')}
                                        borderRadius="xl"
                                        py={2.5}
                                        px={3}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        bg="transparent"
                                        color={useColorModeValue("gray.800", "gray.100")}
                                        _hover={{ bg: useColorModeValue("cyan.50", "whiteAlpha.150"), color: useColorModeValue("cyan.700", "cyan.300") }}
                                        _focus={{ bg: useColorModeValue("cyan.50", "whiteAlpha.150"), color: useColorModeValue("cyan.700", "cyan.300") }}
                                    >
                                        Download Survey Template
                                    </MenuItem>
                                    <MenuDivider borderColor={useColorModeValue("gray.200", "whiteAlpha.150")} my={1.5} />
                                    <MenuItem 
                                        icon={<FontAwesomeIcon icon={faUpload} />} 
                                        onClick={exportToCsv}
                                        isDisabled={!content.quizData.questions.length}
                                        borderRadius="xl"
                                        py={2.5}
                                        px={3}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        bg="transparent"
                                        color={useColorModeValue("gray.800", "gray.100")}
                                        _hover={{ bg: useColorModeValue("teal.50", "whiteAlpha.150"), color: useColorModeValue("teal.700", "cyan.300") }}
                                        _focus={{ bg: useColorModeValue("teal.50", "whiteAlpha.150"), color: useColorModeValue("teal.700", "cyan.300") }}
                                    >
                                        Export to CSV
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>

                        <Button 
                            variant="outline" 
                            colorScheme="red" 
                            size="sm" 
                            borderRadius="xl"
                            leftIcon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={onClearConfirmOpen}
                            isDisabled={!content.quizData.questions.length}
                        >
                            Clear All
                        </Button>
                    </Flex>
                </Flex>

                <Flex direction={{ base: "column", sm: "row" }} justify="space-between" align={{ base: "stretch", sm: "center" }} gap={4} mb={8}>
                    <InputGroup size="lg" maxW={{ base: "100%", md: "600px" }}>
                        <InputLeftElement pointerEvents="none">
                            <FontAwesomeIcon icon={faSearch} color="gray" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search questions by keyword, text, options..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            borderRadius="2xl"
                            bg={useColorModeValue("white", "gray.800")}
                            border="1px solid"
                            borderColor={useColorModeValue("gray.200", "gray.700")}
                            _focus={{ borderColor: "green.400", shadow: "md" }}
                        />
                        {searchQuery && (
                            <InputRightElement>
                                <IconButton
                                    aria-label="Clear search"
                                    icon={<FontAwesomeIcon icon={faTimes} />}
                                    size="sm"
                                    variant="ghost"
                                    borderRadius="full"
                                    onClick={() => setSearchQuery("")}
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>
                    {searchQuery.trim() !== "" && (
                        <Badge colorScheme="green" p={2.5} px={4} borderRadius="xl" fontSize="sm" fontWeight="bold" shadow="sm" alignSelf="center">
                            {filteredQuestions.length} of {content.quizData.questions.length} questions found
                        </Badge>
                    )}
                </Flex>

                <Flex align="center" justify="space-between" mb={2}>
                    {content.quizData?.questions && content.quizData.questions.length > 0 && (
                        <Button 
                            size="sm" 
                            variant="ghost" 
                            colorScheme="green" 
                            borderRadius="xl"
                            onClick={() => {
                                if (content.quizData?.questions && selectedQuestionIds.length === content.quizData.questions.length) {
                                    setSelectedQuestionIds([]);
                                } else if (content.quizData?.questions) {
                                    setSelectedQuestionIds(content.quizData.questions.map(q => q.id));
                                }
                            }}
                        >
                            {content.quizData?.questions && selectedQuestionIds.length === content.quizData.questions.length ? "Deselect All" : "Select All Questions"}
                        </Button>
                    )}
                    {selectedQuestionIds.length > 0 && (
                        <Flex align="center" gap={3} p={2} px={4} bg="red.50" _dark={{ bg: "rgba(229, 62, 62, 0.15)" }} borderRadius="xl" border="1px solid" borderColor="red.100">
                            <Text fontSize="sm" fontWeight="semibold" color="red.700" _dark={{ color: "red.300" }}>
                                {selectedQuestionIds.length} question(s) selected
                            </Text>
                            <Button 
                                size="xs" 
                                colorScheme="red" 
                                borderRadius="lg"
                                leftIcon={<FontAwesomeIcon icon={faTrash} />}
                                onClick={() => {
                                    if (content.quizData?.questions && window.confirm(`Delete the ${selectedQuestionIds.length} selected question(s)?`)) {
                                        const remaining = content.quizData.questions.filter(item => !selectedQuestionIds.includes(item.id));
                                        updateQuestions(remaining);
                                        setSelectedQuestionIds([]);
                                        toast({
                                            title: `Deleted ${selectedQuestionIds.length} question(s)`,
                                            status: "success",
                                            duration: 3000,
                                            isClosable: true
                                        });
                                    }
                                }}
                            >
                                Delete Selected
                            </Button>
                        </Flex>
                    )}
                </Flex>

                <VStack spacing={6} align="stretch">
                    {filteredQuestions.length === 0 ? (
                        <Box 
                            bg={useColorModeValue("white", "gray.800")}
                            borderRadius="3xl"
                            p={10}
                            textAlign="center"
                            shadow="md"
                            border="1px dashed"
                            borderColor={useColorModeValue("gray.300", "gray.600")}
                        >
                            <Text fontSize="lg" fontWeight="semibold" color="gray.500" mb={4}>
                                No questions match "{searchQuery}"
                            </Text>
                            <Button 
                                size="sm" 
                                colorScheme="green" 
                                variant="outline" 
                                borderRadius="xl"
                                onClick={() => setSearchQuery("")}
                            >
                                Clear Search
                            </Button>
                        </Box>
                    ) : (
                        filteredQuestions.map(({ q, originalIndex }) => (
                            <QuestionCard
                                key={q.id}
                                q={q}
                                originalIndex={originalIndex}
                                isSelected={selectedQuestionIds.includes(q.id)}
                                onToggleSelect={handleToggleSelect}
                                onDelete={deleteQuestion}
                                onUpdateQuestion={updateQuestion}
                                onUpdateOption={updateOption}
                                onUpdateWrongExplanation={updateWrongExplanation}
                                onImageUpload={handleImageUpload}
                                onAudioUpload={handleAudioUpload}
                                onRemoveImage={removeImage}
                                onRemoveAudio={removeAudio}
                                onOpenAnnotator={handleOpenEditor}
                                onOpenDescription={setDescriptionTarget}
                                onOpenLightbox={handleOpenLightbox}
                                s3={s3}
                            />
                        ))
                    )}

                    <Flex gap={3} w="100%" direction={{ base: "column", md: "row" }}>
                        <Button
                            leftIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => addQuestion('type1')}
                            size="md"
                            flex={1}
                            height="60px"
                            borderRadius="2xl"
                            border="2px dashed"
                            borderColor={useColorModeValue("teal.300", "teal.600")}
                            bg={useColorModeValue("teal.50/50", "rgba(20, 184, 166, 0.1)")}
                            color={useColorModeValue("teal.700", "teal.300")}
                            _hover={{ bg: useColorModeValue("teal.100/60", "rgba(20, 184, 166, 0.2)"), transform: "translateY(-1px)" }}
                            _active={{ transform: "scale(0.99)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                            fontSize="sm"
                        >
                            + Add Graded Question
                        </Button>
                        <Button
                            leftIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => addQuestion('survey')}
                            size="md"
                            flex={1}
                            height="60px"
                            borderRadius="2xl"
                            border="2px dashed"
                            borderColor={useColorModeValue("cyan.300", "cyan.600")}
                            bg={useColorModeValue("cyan.50/50", "rgba(14, 165, 233, 0.1)")}
                            color={useColorModeValue("cyan.700", "cyan.300")}
                            _hover={{ bg: useColorModeValue("cyan.100/60", "rgba(14, 165, 233, 0.2)"), transform: "translateY(-1px)" }}
                            _active={{ transform: "scale(0.99)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                            fontSize="sm"
                        >
                            + Add Survey Question
                        </Button>
                        <Button
                            as="label"
                            leftIcon={<FontAwesomeIcon icon={faUpload} />}
                            size="md"
                            flex={1}
                            height="60px"
                            borderRadius="2xl"
                            border="2px dashed"
                            borderColor={useColorModeValue("blue.300", "blue.600")}
                            bg={useColorModeValue("blue.50/50", "rgba(66, 153, 225, 0.1)")}
                            color={useColorModeValue("blue.700", "blue.300")}
                            _hover={{ bg: useColorModeValue("blue.100/60", "rgba(66, 153, 225, 0.2)"), transform: "translateY(-1px)" }}
                            _active={{ transform: "scale(0.99)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                            fontSize="sm"
                            cursor="pointer"
                        >
                            Import from CSV/Excel
                            <input
                                type="file"
                                hidden
                                accept=".csv"
                                ref={fileInputRef}
                                onChange={handleCsvUpload}
                            />
                        </Button>
                    </Flex>
                </VStack>
            </Box>



            <Modal isOpen={isAnnotateOpen} onClose={handleCloseAnnotator} size="full" motionPreset="none" trapFocus={false}>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent bg="gray.900" color="white" h="100vh" m={0} borderRadius={0}>
                    <ModalCloseButton zIndex={9999} color="white" bg="blackAlpha.500" _hover={{ bg: "blackAlpha.800" }} />
                    <ModalBody p={6} h="100%" position="relative" display="flex" justifyContent="center" alignItems="center">
                        {annotateTarget && (
                            <Box w="100%" maxW="1400px" h="90vh">
                                <ImageAnnotator
                                    imageUrl={annotateTarget.url}
                                    initialState={
                                        (content?.quizData?.questions[annotateTarget.qIndex]?.images?.[annotateTarget.imgIndex] as any)?.markerState
                                    }
                                    onSave={async (annotatedImageUrl, state) => {
                                        handleCloseAnnotator();
                                        const saveToastId = toast({
                                            title: "Saving annotation...",
                                            status: "info",
                                            duration: null,
                                            isClosable: false,
                                            position: "top-right"
                                        });
                                        try {
                                            const base64Data = annotatedImageUrl.split(',')[1];
                                            const fileName = `annotated_${v4()}.png`;
                                            const response = await apiPostRequest<any, { imageUrl: string, fileKey: string }>('/upload-image', {
                                                fileName,
                                                fileContent: base64Data
                                            });
                                            const newQuestions = [...(content?.quizData?.questions || [])];
                                            const q = newQuestions[annotateTarget.qIndex];
                                            if (q && q.images && q.images[annotateTarget.imgIndex]) {
                                                const img = q.images[annotateTarget.imgIndex] as any;
                                                if (!img.originalUrl) {
                                                    img.originalUrl = img.url;
                                                }
                                                img.url = response.imageUrl;
                                                img.markerState = state;
                                            }
                                            updateQuestions(newQuestions);
                                            toast.update(saveToastId, {
                                                title: "Annotation saved!",
                                                status: "success",
                                                duration: 3000,
                                                isClosable: true
                                            });
                                        } catch (error) {
                                            console.error("Failed to upload annotated image:", error);
                                            toast.update(saveToastId, {
                                                title: "Failed to save annotation",
                                                description: "Please try again.",
                                                status: "error",
                                                duration: 5000,
                                                isClosable: true
                                            });
                                        }
                                    }}
                                    onCancel={handleCloseAnnotator}
                                />
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Modal isOpen={isClearConfirmOpen} onClose={onClearConfirmClose}>
                <ModalOverlay />
                <ModalContent borderRadius="2xl">
                    <ModalHeader>Clear All Questions?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Text mb={6}>Are you sure you want to delete all questions in this quiz? This action cannot be undone.</Text>
                        <Flex justify="flex-end" gap={3}>
                            <Button onClick={onClearConfirmClose} borderRadius="xl">Cancel</Button>
                            <Button colorScheme="red" onClick={handleClearAllQuestions} borderRadius="xl">Clear All</Button>
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Single Question Delete Confirmation Modal */}
            <Modal isOpen={questionToDelete !== null} onClose={() => setQuestionToDelete(null)} isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl">
                    <ModalHeader>Delete Question?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Text mb={6}>
                            Are you sure you want to delete Question #{questionToDelete !== null ? questionToDelete + 1 : ""}? This action cannot be undone.
                        </Text>
                        <Flex justify="flex-end" gap={3}>
                            <Button onClick={() => setQuestionToDelete(null)} borderRadius="xl">Cancel</Button>
                            <Button
                                colorScheme="red"
                                borderRadius="xl"
                                onClick={() => {
                                    if (questionToDelete !== null) {
                                        confirmDeleteQuestion(questionToDelete);
                                        setQuestionToDelete(null);
                                    }
                                }}
                            >
                                Delete Question
                            </Button>
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Modal isOpen={descriptionTarget !== null} onClose={() => setDescriptionTarget(null)} size="lg">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl">
                    <ModalHeader>Image Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {descriptionTarget && content?.quizData?.questions[descriptionTarget.qIndex]?.images?.[descriptionTarget.imgIndex] && (
                            <VStack spacing={4} align="stretch">
                                <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={2} bg="gray.50" display="flex" justifyContent="center">
                                    <SignedImage 
                                        s3Client={s3} 
                                        src={content.quizData?.questions?.[descriptionTarget.qIndex]?.images?.[descriptionTarget.imgIndex]?.url || ""} 
                                        alt="Quiz preview" 
                                        objectFit="contain" 
                                        maxH="240px" 
                                    />
                                </Box>
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="bold">Image Description</FormLabel>
                                    <Textarea
                                        rows={6}
                                        placeholder="Type more information or a detailed explanation about what this image represents. This will be shown to users when they tap the Info 'i' button on mobile."
                                        value={content.quizData?.questions?.[descriptionTarget.qIndex]?.images?.[descriptionTarget.imgIndex]?.description || ""}
                                        onChange={(e) => {
                                            const newQuestions = [...(content.quizData?.questions || [])];
                                            const q = newQuestions[descriptionTarget.qIndex];
                                            if (q && q.images && q.images[descriptionTarget.imgIndex]) {
                                                q.images[descriptionTarget.imgIndex] = {
                                                    ...q.images[descriptionTarget.imgIndex],
                                                    description: e.target.value
                                                };
                                            }
                                            updateQuestions(newQuestions);
                                        }}
                                        borderRadius="xl"
                                        border="2px solid"
                                        borderColor="gray.200"
                                        _focus={{ borderColor: "green.400" }}
                                    />
                                </FormControl>
                                <Flex justify="flex-end" pt={2}>
                                    <Button colorScheme="green" onClick={() => setDescriptionTarget(null)} borderRadius="xl" px={8}>
                                        Done
                                    </Button>
                                </Flex>
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Image Preview Lightbox Modal */}
            <Modal isOpen={previewLightboxUrl !== null} onClose={() => setPreviewLightboxUrl(null)} size="3xl" isCentered>
                <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.800" />
                <ModalContent borderRadius="2xl" overflow="hidden" bg="transparent" shadow="none">
                    <ModalCloseButton color="white" bg="blackAlpha.700" borderRadius="full" zIndex={10} />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {previewLightboxUrl && (
                            <SignedImage 
                                s3Client={s3} 
                                src={previewLightboxUrl} 
                                alt="Medical Reference Preview" 
                                maxH="80vh" 
                                w="auto" 
                                objectFit="contain" 
                                borderRadius="2xl" 
                                shadow="2xl" 
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    )
}
